import { DSNotification } from "@hundred5/design-system";
import { Trans, useLingui } from "@lingui/react/macro";
import { useCallback } from "react";

import { IFlashMessage } from "../flash-messages.types";
import { useFlashMessages } from "../flash-messages.utils";

interface MessageProps {
  message: IFlashMessage;
}

export const Message = ({ message }: MessageProps) => {
  const { i18n, t } = useLingui();
  const { id, content } = message;
  const { removeMessage } = useFlashMessages();
  const close = useCallback(() => removeMessage(id), [id, removeMessage]);

  switch (content.type) {
    case "network_error":
      return (
        <DSNotification
          type="failure"
          title={t({
            message: "Network error",
            comment: "Error: something is wrong with the network connection",
          })}
          content={t`There's a network disconnect. Try checking your network connection.`}
          onClose={close}
        />
      );
    case "recaptcha_error":
      return (
        <DSNotification
          type="failure"
          title={t({
            message: "reCAPTCHA error",
            comment: "Error: something is wrong with the reCAPTCHA",
          })}
          content={t`We couldn't connect with reCaptcha service. Try checking your network connection.`}
          onClose={close}
        />
      );
    case "api_error":
      const supportEmail = "support@hire.toggl.com";
      return (
        <DSNotification
          type="failure"
          title={t({
            message: "Unexpected error",
            comment: "Error: something unexpected happened",
          })}
          content={
            <>
              <Trans>
                We can't determine the cause of error. Please contact us at
              </Trans>{" "}
              <a
                href={`mailto:${supportEmail}${
                  content.errorId ? `?subject=Error ID: ${content.errorId}` : ""
                }`}
              >
                {supportEmail}.
              </a>
              <br />
              {content.errorId && <code>Error ID: {content.errorId}</code>}
            </>
          }
          onClose={close}
        />
      );

    case "logged_in":
      return (
        <DSNotification
          type="success"
          title={t({
            message: "Logged in!",
            comment: "Message shown after user logs in",
          })}
          autoClose={true}
          onClose={close}
        />
      );

    case "unauthorized_error":
      return (
        <DSNotification
          type="warning"
          title={t({
            message: "Session expired",
            comment: "Message shown after user gets logged out automatically",
          })}
          content={t`Your session has expired, please log in again.`}
          onClose={close}
        />
      );

    case "file_too_large_error":
      const maxUploadSize = i18n.number(15, {
        style: "unit",
        unit: "megabyte",
      });

      return (
        <DSNotification
          type="failure"
          title={t({
            message: "File too large",
            comment: "Error: uploaded file is too large",
          })}
          content={
            <>
              <Trans>This file exceeds the maximum upload size of </Trans>{" "}
              {maxUploadSize}. <Trans>Please upload a smaller file.</Trans>
            </>
          }
          onClose={close}
        />
      );

    case "already_finished_error":
      return (
        <DSNotification
          type="warning"
          title={t({
            message: "Already finished",
            comment: "Error: the candidate has already finished the test",
          })}
          content={<Trans>You have already finished this test.</Trans>}
          onClose={close}
        />
      );

    case "timeout":
      return (
        <DSNotification
          type="warning"
          title={t({
            message: "Out of time",
            comment: "Error: the candidate has run out of time",
          })}
          content={
            <p>
              <Trans>
                Sorry, you're out of time. Your answers will be submitted
                automatically.
              </Trans>
            </p>
          }
          onClose={close}
        />
      );

    case "email_sent":
      return (
        <DSNotification
          type="success"
          title={t`Email sent!`}
          autoClose={true}
          onClose={close}
        />
      );

    case "invalid_email":
      return (
        <DSNotification
          type="warning"
          title={t({
            message: "Invalid email address",
            comment: "Error: entered email address is not correct",
          })}
          onClose={close}
          content={
            <p>
              <Trans>
                Sorry, you can not use the temporary email address. Please use
                another email provider.
              </Trans>
            </p>
          }
        />
      );

    case "upload_error":
      return (
        <DSNotification
          type="warning"
          title={t({
            message: "Failed to upload file",
          })}
          onClose={close}
          content={
            <p>
              <Trans>Please try again.</Trans>
            </p>
          }
        />
      );

    case "unsupported_file_type":
      return (
        <DSNotification
          type="failure"
          title={t({
            message: "Unsupported file type",
          })}
          onClose={close}
          content={
            <p>
              <Trans>
                This file format isn't supported. Please use one of the
                following file formats: {content.supportedTypes}.
              </Trans>
            </p>
          }
        />
      );

    default:
      return null;
  }
};
